// VH11Najdi.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import SearchWithAutocomplete from "./SearchWithAutoComplete";

/** Token helper (same logic as your Search component) */
async function getGuestToken() {
  const cached = sessionStorage.getItem("guest_token");
  if (cached) return cached;

  const res = await fetch("https://vikingdev.eu/api/auth/guest", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to get guest token");
  const data = await res.json();

  const token = data.token;
  sessionStorage.setItem("guest_token", token);
  return token;
}

async function fetchProvidersForResultId(resultId, signal) {
  const token = await getGuestToken();
  const url = `https://vikingdev.eu/api/category/-1/${encodeURIComponent(
    resultId
  )}/providers`;

  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });

  if (!res.ok) throw new Error("Failed to load providers");
  return res.json();
}

/** Card grid section (keeps your soft/hover design language) */
function ProvidersSection({
  queryLabel,
  loading,
  error,
  providers,
  onClear,
}) {
  if (!queryLabel) return null;

  return (
    <section className="bg-white rounded-t-[40px] -mt-20 relative z-5 pt-20 pb-20">
      <div className="max-w-7xl mx-auto px-5">
        {/* Header row */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="text-sm text-gray-500">Rezultati za</p>
            <h3 className="text-3xl md:text-4xl font-semibold text-gray-800">
              {queryLabel}
            </h3>
            <p className="text-gray-600 mt-2">
              Izberi ponudnika ali storitev spodaj.
            </p>
          </div>

          <button
            onClick={onClear}
            className="bg-white text-gray-700 px-6 py-3 rounded-full font-semibold text-sm shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 border border-gray-100 w-fit">
            Počisti rezultate
          </button>
        </div>

        {/* States */}
        {loading && (
          <div className="rounded-2xl border border-gray-100 bg-gray-50 px-6 py-5 text-gray-600">
            Nalagam ponudnike…
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-5 text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && (!providers || providers.length === 0) && (
          <div className="rounded-2xl border border-gray-100 bg-gray-50 px-6 py-5 text-gray-600">
            Ni najdenih ponudnikov za ta izbor.
          </div>
        )}

        {/* Cards */}
        {!loading && !error && providers?.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {providers.map((p, index) => {
              // ✅ adjust these to your actual provider shape
              const name =
                p?.name ?? p?.title ?? p?.companyName ?? "Ponudnik";
              const subtitle =
                p?.categoryName ??
                p?.shortDescription ??
                p?.address ??
                "Slovenija";
              const description =
                p?.description ?? p?.about ?? p?.summary ?? "";
              const rating =
                typeof p?.rating === "number" ? p.rating : null;

              return (
                <a
                  key={p?.id ?? `${name}-${index}`}
                  href={p?.website ?? "#"}
                  target="_blank"
                  className="text-center p-8 rounded-2xl transition-all hover:-translate-y-1 hover:bg-indigo-50 cursor-pointer shadow-sm hover:shadow-md border border-gray-100 bg-white"
                >
                  <div className="text-6xl mb-5 inline-block transition-transform hover:scale-110">
                    {p?.logoUrl ? (
                      <img
                        src={p.logoUrl}
                        alt={p.name || "Logo"}
                        target="_blank"
                        className="h-20 w-auto mx-auto object-contain"
                      />
                    ) : (
                      "🏢"
                    )}
                  </div>

                  <h4 className="text-2xl font-bold mb-2 text-gray-800">
                    {name}
                  </h4>

                  <div className="text-sm text-gray-500 mb-4">
                    {subtitle}
                    {rating !== null && (
                      <span className="ml-2 text-indigo-700 font-semibold">
                        ★ {rating.toFixed(1)}
                      </span>
                    )}
                  </div>

                  <a
                    href={p.website}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 inline-block text-indigo-600 font-semibold hover:text-purple-600 transition-colors">
                    Odpri
                  </a>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export default function VH11Najdi() {
  const [selectedLabel, setSelectedLabel] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersError, setProvidersError] = useState("");
  const [providers, setProviders] = useState([]);

  const abortProvidersRef = useRef(null);

  const popularSearches = [
    "menjava pnevmatik",
    "računovodski servis",
    "les za teraso",
    "darilo za rojstni dan",
  ];

  const features = [
    {
      icon: "📍",
      title: "Preverjeni ponudniki",
      description:
        "V našo bazo ne vpisujemo vsega povprek. Vsako podjetje je ročno preverjeno in izbrano na podlagi njihove digitalne prisotnosti in resnosti.",
    },
    {
      icon: "🔩",
      title: "Kirurška natančnost",
      description:
        "Pozabite na tisoče neustreznih zadetkov. Naš sistem podkategorij vas vodi direktno do specializiranih ponudnikov, ki jih dejansko potrebujete.",
    },
    {
      icon: "🚀",
      title: "Brez posrednikov",
      description:
        "Povežemo vas neposredno s spletno stranjo ali drugim profilom ponudnika. Brez provizij, brez čakanja - pot do rešitve je popolnoma preprosta.",
    },
  ];

  const partnerLogos = ["LOGO 1", "LOGO 2", "LOGO 3", "LOGO 4", "LOGO 5"];

  const footerSections = [
    {
      title: "e2f.si",
      links: [
        { label: "Naša vizija in delovanje iskalnika", href: "/o-nas" },
        { label: "Za Podjetja", href: "/za-podjetja" },
        { label: "Pogoji uporabe", href: "/pogoji-uporabe" },
      ],
    },
    {
      title: "Ekosistem Vh11",
      links: [
        { label: "turizem-slovenija.si", href: "https://turizem-slovenija.si" },
        { label: "metuzalem.net", href: "https://metuzalem.net" },
        { label: "e2f.si", href: "https://e2f.si" },
      ],
    },
  ];

  const getLabel = (item) =>
    typeof item === "string"
      ? item
      : item?.name ?? item?.title ?? item?.text ?? "";

  const getId = (item) =>
    typeof item === "object"
      ? item?.id ?? item?.categoryId ?? item?.value
      : null;

  const handlePickAutocomplete = async (item) => {
    const label = getLabel(item);
    const id = getId(item);

    setSelectedLabel(label);
    setSelectedId(id);

    setProviders([]);
    setProvidersError("");

    if (!id) {
      setProvidersError("Izbran rezultat nima ID-ja (pričakujem item.id).");
      return;
    }

    // cancel previous providers request
    if (abortProvidersRef.current) abortProvidersRef.current.abort();
    const controller = new AbortController();
    abortProvidersRef.current = controller;

    try {
      setProvidersLoading(true);
      const data = await fetchProvidersForResultId(id, controller.signal);

      // adjust if your API wraps it
      const list = Array.isArray(data) ? data : data?.results ?? data?.providers ?? [];
      setProviders(list);
    } catch (e) {
      if (e.name === "AbortError") return;
      setProvidersError(e?.message ?? "Napaka pri nalaganju ponudnikov.");
    } finally {
      setProvidersLoading(false);
    }
  };

  const clearResults = () => {
    setSelectedLabel("");
    setSelectedId(null);
    setProviders([]);
    setProvidersError("");
    setProvidersLoading(false);
    if (abortProvidersRef.current) abortProvidersRef.current.abort();
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: "linear-gradient(135deg, #005d7e, #022e59, #fafafa)",
      }}
    >
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeInUp { animation: fadeInUp 0.8s ease forwards; }
        .animate-delay-200 { animation-delay: 0.2s; opacity: 0; }
        .animate-delay-400 { animation-delay: 0.4s; opacity: 0; }
        .animate-delay-600 { animation-delay: 0.6s; opacity: 0; }
      `}</style>

      {/* Header */}
      <header className="relative z-10">
        <div className="max-w-7xl mx-auto px-5 py-5">
          <div className="flex justify-center items-center">
            <div className="text-3xl font-bold text-gray-700 tracking-tight">
              <a href="/">
                <img
                  src="/e2fsilogo.png"
                  alt="Logo"
                  style={{ width: "200px" }}
                />
              </a>
            </div>
            {/**<nav className="flex items-center gap-8">
              <a
                href="#"
                className="text-white font-medium text-sm hover:text-white transition-all hover:-translate-y-0.5 hidden md:block"
              >
                O nas
              </a>
              <a
                href="#"
                className="bg-white text-gray-700 px-6 py-3 rounded-full font-semibold text-sm shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
              >
                + Dodaj podjetje
              </a>
            </nav>*/}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main>
        <section className="text-center px-5 pt-32 pb-48">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl text-white font-normal mb-12 animate-fadeInUp animate-delay-200">
              Slovenski iskalnik izdelkov in storitev.
            </h3>

            {/* Search Bar */}
            <div className="max-w-3xl mx-auto mb-8 animate-fadeInUp animate-delay-400">
              <SearchWithAutocomplete onSelect={handlePickAutocomplete} />
            </div>

            <div className="text-sm text-gray-300 animate-fadeInUp animate-delay-600" style={{ "zIndex": "50" }}>
              <strong className="text-white font-semibold" style={{ "zIndex": "50" }}>
                Priljubljena iskanja:
              </strong>{" "}
              {popularSearches.join(", ")}
            </div>
          </div>
        </section>

        {/* ✅ NEW: Providers grid AFTER choosing an autocomplete result */}
        <ProvidersSection
          queryLabel={selectedLabel}
          loading={providersLoading}
          error={providersError}
          providers={providers}
          onClear={clearResults}
        />

        {/* Features Section (kept exactly in your style) */}
        {providers?.length <= 0 && (
          <section className="bg-white rounded-t-[40px] -mt-0 relative pt-20 pb-20">
            <div className="max-w-7xl mx-auto px-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="text-center p-8 rounded-2xl transition-all hover:-translate-y-1 hover:bg-indigo-50 cursor-pointer"
                  >
                    <div className="text-6xl mb-5 inline-block transition-transform hover:scale-110">
                      {feature.icon}
                    </div>
                    <h4 className="text-2xl font-bold mb-4 text-gray-800">
                      {feature.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed text-lg">
                      {feature.description}

                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}



        {/* Partners Section (kept) */}
        {providers?.length <= 0 && (<section className="bg-white text-center px-5 py-16 pb-20">
          <div className="max-w-7xl mx-auto">
            <h3 className="text-3xl font-semibold mb-12 text-gray-700">
              Nekatera podjetja, ki jih najdete pri nas:
            </h3>
            <div className="flex flex-wrap justify-center items-center gap-8">
              {/* Pulse Coding */}
              <a
                href="https://pulsecoding.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-5 bg-black rounded-xl transition-all hover:scale-105 cursor-pointer flex items-center"
              >
                <img
                  src="https://pulsecoding.eu/wp-content/uploads/2023/01/pulse_logo_novi.png"
                  alt="Pulse Coding"
                  className="h-10 w-auto"
                />
              </a>

              {/* Slovenia Tourism */}
              <a
                href="https://turizem-slovenija.si"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-5 bg-gray-50 rounded-xl transition-all hover:scale-105 cursor-pointer flex items-center"
              >
                <img
                  src="https://turizem-slovenija.si/images/turizems_logo_black.png"
                  alt="Slovenia Tourism"
                  className="h-10 w-auto"
                />
              </a>

              {/* AirCon */}
              <a
                href="https://www.aircon.si"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-5 bg-gray-50 rounded-xl transition-all hover:scale-105 cursor-pointer flex items-center"
              >
                <img
                  src="https://www.aircon.si/wp-content/uploads/2020/05/aircon_margin.svg"
                  alt="AirCon"
                  className="h-10 w-auto"
                />
              </a>

              {/* LEDSTAR */}
              <a
                href="https://www.ledstar.si/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-5 bg-gray-50 rounded-xl transition-all hover:scale-105 cursor-pointer flex items-center"
              >
                <img
                  src="https://www.ledstar.si/wp-content/uploads/2023/02/LEDSTAR_logo_-2023.png"
                  alt="LEDSTAR"
                  className="h-10 w-auto"
                />
              </a>
            </div>


          </div>
        </section>)}
      </main>

      {/* Footer (kept) */}
      <footer className="bg-gray-900 text-white px-5 py-16 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">

            {/* LOGO + ABOUT */}
            <div>
              <div className="text-3xl font-bold mb-4 tracking-tight">
                <a href="/">
                  <img
                    src="/easytofind-01.png"
                    alt="Logo"
                    style={{ width: "160px" }}
                  />
                </a>
              </div>

              <p className="text-white/70 text-sm leading-relaxed">
                e2f.si je slovenski iskalnik izdelkov in storitev.
                Pomagamo uporabnikom hitro najti podjetja, obrtnike in ponudnike
                po vsej Sloveniji.
              </p>

              <p><a href="mailto:info@e2f.si">info@e2f.si</a></p>
            </div>

            {/* DINAMIČNE SEKCIJE FOOTERJA */}
            {footerSections.map((section, index) => (
              <div key={index}>
                <h5 className="text-xl font-semibold mb-5">
                  {section.title}
                </h5>

                <ul className="space-y-3">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <a
                        href={link.href}
                        target={link.href.startsWith("http") ? "_blank" : "_self"}
                        rel={link.href.startsWith("http") ? "noreferrer" : undefined}
                        className="text-white/70 hover:text-white transition-all hover:pl-1 inline-block"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* SOCIAL */}
            <div>
              <h5 className="text-xl font-semibold mb-5">Sledi nam</h5>

              <ul className="space-y-3">
                <li>
                  <a
                    href="https://www.facebook.com/profile.php?id=61587192184775"
                    target="_blank"
                    rel="noreferrer"
                    className="text-white/70 hover:text-white transition-all hover:pl-1 inline-block"
                  >
                    Facebook
                  </a>
                </li>
              </ul>
            </div>

          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 text-center text-sm text-white/60">
          © {new Date().getFullYear()} e2f.si. Vse pravice pridržane. <br />
          Vsi logotipi in blagovne znamke prikazani na strani so last njihovih zakonitih lastnikov
          in niso v lasti lastnika spletne strani ali te spletne strani.
        </div>
      </footer>
    </div>
  );
}
